import type {
  ConnectorParameterUndoEntry,
  ConnectorParameterUndoPlan,
  ConnectorProjectParameterUndoEntry,
} from '../../../types/parameterSetupUndo';
import { countUndoPlanEntries, countUndoPlanFamilies } from '../../parameterUndo/undoPlanCounts';
import type { ParameterAdditionResult } from './models';

type PublishUndoPlan = (plan: ConnectorParameterUndoPlan | null) => void;

export interface ConnectorUndoCheckpoint {
  readonly plan: ConnectorParameterUndoPlan | null;
  recordProjectResult(result: ParameterAdditionResult): string | null;
  recordFamilyResult(result: ParameterAdditionResult): string | null;
}

export function createConnectorUndoCheckpoint(
  projectKey: string,
  publish: PublishUndoPlan,
): ConnectorUndoCheckpoint {
  const projectEntries = new Map<string, ConnectorProjectParameterUndoEntry>();
  const familyEntries = new Map<string, ConnectorParameterUndoEntry>();
  const id = `connector-undo-${Date.now()}-${Math.round(Math.random() * 1000)}`;
  const createdAt = new Date().toISOString();

  const buildPlan = (): ConnectorParameterUndoPlan | null => {
    const currentProjectEntries = [...projectEntries.values()];
    const currentFamilyEntries = [...familyEntries.values()];
    if (currentProjectEntries.length === 0 && currentFamilyEntries.length === 0) {
      return null;
    }
    return {
      id,
      createdAt,
      projectKey,
      projectParameterNames: currentProjectEntries.map((entry) => entry.parameterName),
      projectEntries: currentProjectEntries,
      familyCount: countUndoPlanFamilies({ entries: currentFamilyEntries }),
      entryCount: countUndoPlanEntries({ entries: currentFamilyEntries }),
      status: 'available',
      entries: currentFamilyEntries,
    };
  };

  const append = (result: ParameterAdditionResult): void => {
    for (const entry of result.projectUndoEntries ?? []) {
      projectEntries.set(entry.parameterName.trim().toLowerCase(), entry);
    }
    for (const entry of result.familyUndoEntries ?? []) {
      familyEntries.set(
        `${entry.familyId.trim().toLowerCase()}::${entry.parameterName.trim().toLowerCase()}`,
        entry,
      );
    }
    const plan = buildPlan();
    if (plan) {
      publish(plan);
    }
  };

  return {
    get plan() {
      return buildPlan();
    },
    recordProjectResult(result) {
      append(result);
      const mutationCount = (result.addedCount || 0) + (result.updatedCount || 0);
      return mutationCount > 0 && (result.projectUndoEntries?.length ?? 0) === 0
        ? 'Revit changed project parameter bindings without returning a complete rollback checkpoint.'
        : null;
    },
    recordFamilyResult(result) {
      append(result);
      const mutationCount = result.modifiedFamilyCount ?? result.addedCount ?? 0;
      return mutationCount > 0 && (result.familyUndoEntries?.length ?? 0) === 0
        ? 'Revit changed family parameter definitions without returning a rollback checkpoint.'
        : null;
    },
  };
}
